import React, { useEffect } from 'react';
import { useCheckoutContext } from '../CheckoutContextProvider';
import { logError } from '../services/logging-service';
import { useStripeElementsContext } from '../StripeElementsContextProvider';

export const ExpressCheckoutButton = ({ className, handleRequestSuccess, checkTransactability }) => {
  const {
    submitRequest,
    paymentForm,
    collectFormsValues,
    quote
  } = useCheckoutContext();

  // Update express checkout config with the latest amounts
  useEffect(() => {
    refreshExpressCheckout();
  }, [quote]);

  const { stripeElements, availablePaymentMethods } = useStripeElementsContext();

  const selectedPaymentMethod = paymentForm.watch('selectedPaymentMethod');

  const refreshExpressCheckout = async () => {
    if (!selectedPaymentMethod || selectedPaymentMethod === 'card' || !stripeElements) {
      return;
    }

    stripeElements?.getElement?.('expressCheckout')?.destroy?.();

    checkTransactability(async () => {
      try {
        const config = availablePaymentMethods?.reduce((acc, wallet) => {
          if (wallet === 'card') {
            return { ...acc };
          }
          return { ...acc, [wallet]: 'never' };
        }, {});

        const expressCheckout = stripeElements?.create('expressCheckout', {
          paymentMethods: {
            ...config,
            [selectedPaymentMethod]: 'auto',
          },
          buttonHeight: 40,
          buttonType: {
            applePay: 'book',
            googlePay: 'book',
          }
        });

        expressCheckout.mount('#express-checkout-element');

        expressCheckout.on('click', handleExpressCheckoutClick);

        return () => {
          expressCheckout.destroy();
        };
      } catch (e) {
        logError('stripe.createExpressElement', e);
      }
    });
  };

  React.useEffect(() => {
    refreshExpressCheckout();
  }, [stripeElements, selectedPaymentMethod, availablePaymentMethods]);

  const handleExpressCheckoutCancel = () => {
    // If payment is not confirmed reinitialize express checkout to get new stripe token
    refreshExpressCheckout();
  };

  const handleExpressCheckoutClick = async (event) => {
    const formValues = await collectFormsValues();
    if (!!formValues && !formValues.error) {
      // If validation passed we add confirm handler for express checkout
      const expressCheckout = stripeElements?.getElement('expressCheckout');
      expressCheckout?.on?.('confirm', () => handleExpressCheckoutConfirm(formValues));
      expressCheckout?.on?.('cancel', handleExpressCheckoutCancel);
      // Proceed to Google/Apple Pay flow
      event.resolve();
    }
  };

  const handleExpressCheckoutConfirm = async (formValues) => {
    // Values are collected during express "click" handler validation
    const requestRes = await submitRequest(formValues);

    if (!requestRes || requestRes.error) {
      // if no response, form validation failed
      return;
    }

    handleRequestSuccess(requestRes);
  };

  return (
    <div
      id="express-checkout-element"
      style={{ maxWidth: 250 }}
      className={className}
    />
  );
};
